import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ChevronDown, ChevronUp, Info } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import InputField from "@/components/form/components/InputField";
import { useAuthStore } from "@/store";
import {
  useCreateRoleMutation,
  useRolePermissionsQuery,
  useRoleTemplatesQuery,
} from "@/hooks/api/role.queries";
import { useAllDesignationsUnfilteredQuery } from "@/hooks/api/designation.queries";
import { PermissionType } from "@/services/role.service";
import { useNavigate } from "react-router-dom";

// ─── Schema ──────────────────────────────────────────────────────────────────

const roleSchema = z.object({
  name: z.string().min(1, "Role name is required"),
  description: z.string().optional(),
  designation_id: z.string().min(1, "Designation is required"),
});

type RoleFormType = z.infer<typeof roleSchema>;

// ─── Types ────────────────────────────────────────────────────────────────────

type ModuleGroup = {
  id: string;
  label: string;
  perms: PermissionType[];
};

// ─── Module Card ──────────────────────────────────────────────────────────────

function ModuleCard({
  module,
  checkedIds,
  onToggleAll,
  onToggleOne,
}: {
  module: ModuleGroup;
  checkedIds: Set<string>;
  onToggleAll: (module: ModuleGroup, on: boolean) => void;
  onToggleOne: (permId: string, checked: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const allChecked = module.perms.every((p) => checkedIds.has(p.id));
  const someChecked = module.perms.some((p) => checkedIds.has(p.id));

  const subtitle = module.perms
    .map((p) => {
      const action = p.name.split(".").slice(1).join(".");
      return action.charAt(0).toUpperCase() + action.slice(1).replace(/_/g, " ");
    })
    .join(", ");

  return (
    <Card className="overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
      <div
        className="flex cursor-pointer items-center gap-4 px-5 py-4"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-bold text-gray-900">{module.label}</span>
          <span className="truncate text-xs text-gray-400">{subtitle}</span>
        </div>
        <Switch
          checked={allChecked || someChecked}
          onCheckedChange={(on) => onToggleAll(module, on)}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 data-[state=checked]:bg-indigo-500"
        />
        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        )}
      </div>
      {expanded && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-gray-100 bg-gray-50/50 px-5 py-4 sm:grid-cols-2">
          {module.perms.map((perm) => {
            const action = perm.name.split(".").slice(1).join(".");
            const label = action.charAt(0).toUpperCase() + action.slice(1).replace(/_/g, " ");
            return (
              <label
                key={perm.id}
                className="flex cursor-pointer items-center gap-2 text-sm text-gray-700"
              >
                <Checkbox
                  checked={checkedIds.has(perm.id)}
                  onCheckedChange={(c) => onToggleOne(perm.id, !!c)}
                  className="rounded border-gray-300"
                />
                {label}
              </label>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Create Role Form ─────────────────────────────────────────────────────────

type CreateRoleFormProps = {
  onSuccess?: (newRoleId: string) => void;
};

const CreateRoleForm = ({ onSuccess }: CreateRoleFormProps = {}) => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  const { data: assignablePermissions = [], isLoading } = useRolePermissionsQuery(
    currentUser?.role_ids?.[0]
  );
  const { data: templates = [] } = useRoleTemplatesQuery();
  const { data: designations = [] } = useAllDesignationsUnfilteredQuery();

  const createMutation = useCreateRoleMutation();

  const form = useForm<RoleFormType>({
    resolver: zodResolver(roleSchema),
    defaultValues: { name: "", description: "", designation_id: "" },
  });

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());


  const designationNameToId = useMemo(
    () => new Map(designations.map((d) => [d.name, d.id])),
    [designations]
  );

  const modules: ModuleGroup[] = useMemo(() => {
    const dict = new Map<string, ModuleGroup>();
    assignablePermissions.forEach((p) => {
      const mod = p.name.split(".")[0];
      if (!dict.has(mod)) {
        const label = mod.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
        dict.set(mod, { id: mod, label, perms: [] });
      }
      dict.get(mod)!.perms.push(p);
    });
    return Array.from(dict.values());
  }, [assignablePermissions]);

  const handleSelectTemplate = (templateId: string) => {
    if (templateId === "__clear__") {
      form.reset({ name: "", description: "", designation_id: "" });
      setCheckedIds(new Set());
      return;
    }
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;
    form.setValue("name", template.name, { shouldDirty: true });
    form.setValue("description", template.description, { shouldDirty: true });
    form.setValue("designation_id", designationNameToId.get(template.designation) ?? "", { shouldDirty: true });
    setCheckedIds(new Set(template.permissions));
  };

  const handleToggleAll = useCallback((module: ModuleGroup, on: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      module.perms.forEach((p) => (on ? next.add(p.id) : next.delete(p.id)));
      return next;
    });
  }, []);

  const handleToggleOne = useCallback((permId: string, checked: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(permId) : next.delete(permId);
      return next;
    });
  }, []);

  const INLINE_DRAFT_KEY = "inline_create_draft";

  const onSubmit = (values: RoleFormType) => {
    createMutation.mutate(
      { ...values, permission_ids: Array.from(checkedIds) },
      {
        onSuccess: (res: any) => {
          form.reset({ name: "", description: "", designation_id: "" });
          setCheckedIds(new Set());
          const newRoleId = res?.data?.id ?? res?.id;
          if (newRoleId) onSuccess?.(newRoleId);
          
          // Universal Navigate & Return Logic
          const draftStr = sessionStorage.getItem(INLINE_DRAFT_KEY);
          if (draftStr && newRoleId) {
            try {
              const draft = JSON.parse(draftStr);
              if (draft.targetField === "role_id") {
                draft.newValue = newRoleId;
                draft.newLabel = values.name;
                sessionStorage.setItem(INLINE_DRAFT_KEY, JSON.stringify(draft));
                if (draft.sourcePath) {
                  navigate(draft.sourcePath);
                }
              }
            } catch (e) {
              console.error("Failed to parse inline draft", e);
            }
          }
        },
      }
    );
  };

  const isPending = createMutation.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {templates.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Use a Template
            </label>
            <Select onValueChange={handleSelectTemplate}>
              <SelectTrigger className="w-full rounded-xl border-gray-200 bg-white shadow-sm">
                <SelectValue placeholder="Select a role template to auto-fill..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__clear__">— Clear selection —</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    <span className="ml-2 text-xs text-gray-400">({t.designation})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Card className="overflow-hidden rounded-2xl border-none shadow-sm">
          <CardContent className="flex flex-col gap-4 p-6">
            <InputField<RoleFormType>
              name="name"
              label="Role Name"
              placeholder="e.g. Finance Admin"
              type="text"
              disabled={isPending}
              required
            />
            <InputField<RoleFormType>
              name="description"
              label="Description"
              placeholder="Briefly describe the purpose of this role..."
              type="text"
              disabled={isPending}
            />
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                Designation <span className="text-destructive">*</span>
              </label>
              <Select
                value={form.watch("designation_id")}
                onValueChange={(v) => form.setValue("designation_id", v, { shouldDirty: true, shouldValidate: true })}
                disabled={isPending}
              >
                <SelectTrigger className="w-full rounded-xl border-gray-200 bg-white">
                  <SelectValue placeholder="Select designation..." />
                </SelectTrigger>
                <SelectContent>
                  {designations.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.designation_id && (
                <p className="text-xs text-destructive">{form.formState.errors.designation_id.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-start gap-3 rounded-2xl bg-indigo-50 px-5 py-4 text-sm text-indigo-700">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>Define what modules users can access. Use the checklist to grant specific capabilities.</p>
        </div>

        {isLoading ? (
          <p className="py-10 text-center text-sm text-gray-400">Loading permissions...</p>
        ) : (
          <div className="flex flex-col gap-3">
            {modules.map((mod) => (
              <ModuleCard
                key={mod.id}
                module={mod}
                checkedIds={checkedIds}
                onToggleAll={handleToggleAll}
                onToggleOne={handleToggleOne}
              />
            ))}
          </div>
        )}

        <div className="flex items-center justify-end border-t border-gray-100 pt-4">
          <Button
            type="submit"
            disabled={isPending || !form.formState.isDirty}
            className="h-9 rounded-md bg-black px-8 text-xs font-bold text-white shadow-sm hover:bg-gray-800 disabled:opacity-50"
          >
            {isPending ? "Saving..." : "Save Role"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CreateRoleForm;
